import type { ReactNode } from 'react';
import { createContext, useContext, useId, useLayoutEffect, useRef, useState } from 'react';

import type { ExportFormat } from '@/hooks/use-export';

export type SearchConfig = {
  placeholder?: string;
  onSearch: (term: string) => void;
  /** Terms shorter than this emit as empty (full list). Omit to fire on every character. */
  minChars?: number;
  /** Hard cap on the search input length. */
  maxLength?: number;
  /** Debounce interval in ms. Omit to keep `<SearchBar>`'s 300 ms default — Reports passes 500
   *  (AC 112312); no other module's timing moves. */
  debounceMs?: number;
};

export type FilterConfig = {
  active: boolean;
  onClick: () => void;
  onClear?: () => void;
};

export type ExportConfig = {
  endpoint: string;
  params: Record<string, string | number | undefined>;
  /** Base filename without extension, e.g. "categories". */
  filename: string;
  /** Restrict the offered export formats; omit for all three. */
  formats?: ExportFormat[];
  /** `'get'` (default) or `'post'` (JSON body) — see `useExport`. */
  method?: 'get' | 'post';
};

export type PrimaryActionConfig = {
  label: string;
  icon?: ReactNode;
  onClick: () => void;
};

export type ModuleHeaderConfig = {
  title: string;
  search?: SearchConfig;
  filter?: FilterConfig;
  export?: ExportConfig;
  primaryAction?: PrimaryActionConfig;
};

/** What the header slot reads: the page's config plus the identity of the page that declared it.
 *  The header outlives the pages beneath it, so `ownerId` is how it tells one page from the next
 *  (ADO 121012). */
export type PublishedModuleHeader = ModuleHeaderConfig & { ownerId: string };

/** A hub's layout-level header: the title only, declared before its lazy tab page loads. */
export type ModuleHeaderBase = { title: string };

/** The base header has no toolbar, so nothing keys off its owner; a fixed id is enough. */
const BASE_OWNER_ID = 'module-header-base';

type ContextValue = {
  base: ModuleHeaderBase | null;
  setBase: (base: ModuleHeaderBase | null) => void;
  config: PublishedModuleHeader | null;
  setConfig: (config: PublishedModuleHeader | null) => void;
};

const ModuleHeaderContext = createContext<ContextValue | null>(null);

export function ModuleHeaderProvider({ children }: { children: ReactNode }) {
  const [base, setBase] = useState<ModuleHeaderBase | null>(null);
  const [config, setConfig] = useState<PublishedModuleHeader | null>(null);
  return (
    <ModuleHeaderContext.Provider value={{ base, setBase, config, setConfig }}>
      {children}
    </ModuleHeaderContext.Provider>
  );
}

/**
 * Read the effective header config (null when none). For the header slot.
 *
 * The active page's config wins; otherwise the hub's title-only base is used, so the header — and
 * the `<ModuleTabs>` below it — paints in its final position the moment the hub chunk loads, not
 * when the lazy tab page's chunk does. Two slots rather than one because React fires a child's
 * effects before its parent's: a shared slot would let the hub overwrite the page's toolbar.
 */
export function useModuleHeaderConfig(): PublishedModuleHeader | null {
  const ctx = useContext(ModuleHeaderContext);
  if (!ctx) return null;
  return ctx.config ?? (ctx.base ? { ownerId: BASE_OWNER_ID, title: ctx.base.title } : null);
}

/**
 * Declare a hub's header title. It must match the title its tab pages pass to
 * {@link useModuleHeader}, so the page's toolbar arriving later enriches the header without
 * moving the tabs.
 */
export function useModuleHeaderBase(base: ModuleHeaderBase): void {
  const ctx = useContext(ModuleHeaderContext);
  if (!ctx) throw new Error('useModuleHeaderBase must be used within <ModuleHeaderProvider>');
  const { setBase } = ctx;

  // Layout effect so the title is published before paint, in the same frame as the tabs.
  useLayoutEffect(() => {
    setBase({ title: base.title });
    return () => setBase(null);
  }, [setBase, base.title]);
}

/**
 * Declare this page's header. Handlers are proxied through a ref so changing
 * their identity every render never retriggers the effect — only a change in
 * the primitive signature (title, which pieces exist, filter.active, export
 * params/endpoint/filename, primaryAction.label) republishes the config.
 */
export function useModuleHeader(config: ModuleHeaderConfig): void {
  const ctx = useContext(ModuleHeaderContext);
  if (!ctx) throw new Error('useModuleHeader must be used within <ModuleHeaderProvider>');
  const { setConfig } = ctx;
  const ownerId = useId();

  const ref = useRef(config);
  // Sync the ref after every render so callbacks always close over the latest config.
  useLayoutEffect(() => {
    ref.current = config;
  });

  const signature = JSON.stringify({
    title: config.title,
    search: config.search
      ? {
          placeholder: config.search.placeholder ?? null,
          minChars: config.search.minChars ?? null,
          maxLength: config.search.maxLength ?? null,
          debounceMs: config.search.debounceMs ?? null,
        }
      : null,
    filter: config.filter
      ? { active: config.filter.active, clearable: Boolean(config.filter.onClear) }
      : null,
    export: config.export
      ? {
          endpoint: config.export.endpoint,
          filename: config.export.filename,
          params: config.export.params,
          formats: config.export.formats ?? null,
          method: config.export.method ?? null,
        }
      : null,
    primaryAction: config.primaryAction ? { label: config.primaryAction.label } : null,
  });

  // Layout effect so the header is published before paint — the page's content never paints
  // first and is then pushed down when the header appears a frame later.
  useLayoutEffect(() => {
    const c = ref.current;
    setConfig({
      ownerId,
      title: c.title,
      search: c.search
        ? {
            placeholder: c.search.placeholder,
            onSearch: (t: string) => ref.current.search?.onSearch(t),
            minChars: c.search.minChars,
            maxLength: c.search.maxLength,
            debounceMs: c.search.debounceMs,
          }
        : undefined,
      filter: c.filter
        ? {
            active: c.filter.active,
            onClick: () => ref.current.filter?.onClick(),
            onClear: c.filter.onClear ? () => ref.current.filter?.onClear?.() : undefined,
          }
        : undefined,
      export: c.export,
      primaryAction: c.primaryAction
        ? {
            label: c.primaryAction.label,
            icon: c.primaryAction.icon,
            onClick: () => ref.current.primaryAction?.onClick(),
          }
        : undefined,
    });
    return () => setConfig(null);
    // ref-proxied handlers are intentionally excluded; only `signature` drives updates.
  }, [setConfig, signature, ownerId]);
}
